/**
 * Loader for the hypothesis-form A/B test picker (AC-1108).
 *
 * Split out of ExperimentsHubPage so the retry contract is unit-testable
 * without a React tree. The distinction it exists to preserve:
 *
 *   []    the project genuinely has no A/B tests  → picker, "Not linked yet"
 *   null  we could not read the list at all       → free-text id fallback
 *
 * Collapsing those two into one value is what let a single transient Firestore
 * `[code=unavailable]` silently swap the picker for a text field on the dev
 * regression of 2026-07-29 — for the rest of the page session, since the
 * effect never refires.
 */
import type { AbTestOption } from './components/HypothesisForm';

/** Minimal structural view of the placement list this loader needs. */
export interface AbTestPlacementSource {
  list(projectId: string): Promise<Array<{
    id: string;
    type?: string;
    name?: string;
    alias?: string;
  }>>;
}

export const AB_TEST_LIST_ATTEMPTS = 3;
export const AB_TEST_LIST_RETRY_MS = 600;

export interface LoadAbTestOptionsOptions {
  attempts?: number;
  retryMs?: number;
  /** Injectable for tests; production uses a real timer. */
  sleep?: (ms: number) => Promise<void>;
}

const realSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Reads the project's `ab_test` placements, retrying transient failures with a
 * linear backoff. Resolves to `null` only once every attempt has failed.
 */
export async function loadAbTestOptions(
  source: AbTestPlacementSource,
  projectId: string,
  options: LoadAbTestOptionsOptions = {},
): Promise<AbTestOption[] | null> {
  const {
    attempts = AB_TEST_LIST_ATTEMPTS,
    retryMs = AB_TEST_LIST_RETRY_MS,
    sleep = realSleep,
  } = options;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const placements = await source.list(projectId);
      return placements
        .filter(placement => placement.type === 'ab_test')
        .map(placement => ({
          id: placement.id,
          name: placement.name || placement.id,
          ...(placement.alias ? { alias: placement.alias } : {}),
        }));
    } catch {
      if (attempt === attempts) return null;
      await sleep(retryMs * attempt);
    }
  }
  return null;
}
